import {MedicineModel} from "@/app/api/medicines";
import {StorageKey, useLocalStorage} from "@/app/utils/local_storage";
import {useCallback} from "react";

declare global {
  interface String {
    toNullIfEmpty(): string | null; // Тип string | null
  }
}

String.prototype.toNullIfEmpty = function (): string | null {
  return this.length > 0 ? this.toString() : null; // Используем this.toString()
};

export function enumValues<T extends object>(enumObj: T): T[keyof T][] {
  return Object.values(enumObj).filter(value => typeof value === 'string') as T[keyof T][];
}

export const prepareImage = (medicineImage: string) => {
  return medicineImage.split(" ")[1];
};

interface ToggleOptions {
  returnBoolean?: boolean;
}

export const useToggleMedicineForCompare = () => {
  const {getLocalStorage, setLocalStorage} = useLocalStorage();

  return useCallback(
    (medicine: MedicineModel, {returnBoolean = false}: ToggleOptions = {}) => {
      const storageValue = getLocalStorage(StorageKey.medicinesToCompare);
      let medicines: MedicineModel[] = [];

      if (storageValue) {
        try {
          medicines = JSON.parse(storageValue);
        } catch (error) {
          medicines = [];
        }
      }

      // Приводим registration_number к строке для сравнения
      const exists = medicines.some(
        (m) => String(m.registration_number) === String(medicine.registration_number)
      );

      let returnValue: boolean = false;
      if (exists) {
        medicines = medicines.filter(
          (m) => String(m.registration_number) !== String(medicine.registration_number)
        );
        returnValue = false;
      } else {
        medicines.push(medicine);
        returnValue = true;
      }

      setLocalStorage(StorageKey.medicinesToCompare, JSON.stringify(medicines));
      return returnBoolean ? returnValue : medicines;
    },
    [getLocalStorage, setLocalStorage]
  );
};

export const capitalizeText = (text: string) => {
  return text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// debounce.ts
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced as T & { cancel: () => void };
}

export function formatDate(input: string): string {
  const date = new Date(input);
  if (isNaN(date.getTime())) {
    return "";
  }
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return formatter.format(date);
}

export function convertCamelCaseToSentence(text: string): string {
  const withSpaces = text.replace(/([A-Z])/g, ' $1');
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
}

export function animateProgress(duration: number, onUpdate: (progress: number) => void) {
  let startTime: number | null = null;

  function easeOut(t: number) {
    return 1 - Math.pow(1 - t, 3); // Ease-out функция для плавного замедления
  }

  function step(timestamp: number) {
    if (!startTime) startTime = timestamp; // Запоминаем начальное время
    const elapsedTime = timestamp - startTime; // Время, прошедшее с начала анимации
    const progress = Math.min(easeOut(elapsedTime / duration) * 90, 90); // Рассчитываем прогресс, ограничивая его 90

    onUpdate(progress); // Обновляем прогресс

    if (elapsedTime < duration) {
      requestAnimationFrame(step); // Продолжаем анимацию, если время ещё не прошло
    }
  }

  requestAnimationFrame(step); // Запускаем анимацию
}

export function convertFloat32ToInt16(buffer: Float32Array): Int16Array {
  const l = buffer.length;
  const buf = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    const s = Math.max(-1, Math.min(1, buffer[i]));
    buf[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return buf;
}

export const arrayBufferToBase64 = (buffer: ArrayBufferLike): string => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export function resampleBuffer(
  buffer: Float32Array,
  origSampleRate: number,
  targetSampleRate: number
): Float32Array {
  if (origSampleRate === targetSampleRate) return buffer;
  const sampleRateRatio = origSampleRate / targetSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = (offsetResult + 1) * sampleRateRatio;
    let accum = 0;
    let count = 0;
    for (
      let i = Math.floor(offsetBuffer);
      i < Math.min(Math.floor(nextOffsetBuffer), buffer.length);
      i++
    ) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

export const copyToClipboard = async (string: string) => {
  if (!string) return;
  await navigator.clipboard.writeText(string);
}


export * from "./custom_function";